import { type AdminUserRow } from "@nexys/user-management-client";
import { AlertCircle, Trash2 } from "lucide-react";
import { useState } from "react";

import { useUmClient } from "../../context/auth-context.js";
import { errorMessage } from "../../lib/error-message.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Button } from "../ui/button.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog.js";

export interface UserDeleteDialogProps {
  /** The user to delete; null hides the dialog. */
  user: AdminUserRow | null;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}

/**
 * Confirm-then-delete (soft delete — the server sets `deletedAt` and
 * revokes the user's sessions; the row stays for audit).
 */
export const UserDeleteDialog = ({ user, onOpenChange, onDeleted }: UserDeleteDialogProps) => {
  const client = useUmClient();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    const res = await client.adminRemoveUser({ userId: user.id });
    setLoading(false);
    if (res.ok) {
      onDeleted?.();
      onOpenChange(false);
    } else {
      setError(errorMessage(res.error, "Couldn't delete user."));
    }
  };

  return (
    <Dialog
      open={user !== null}
      onOpenChange={(o) => {
        if (!o) setError(null);
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {user?.name || user?.email}?</DialogTitle>
          <DialogDescription>
            The account is deactivated and all of its sessions are revoked.
            The user can no longer sign in. This keeps the record for audit
            purposes (soft delete).
          </DialogDescription>
        </DialogHeader>
        {error ? (
          <Alert variant="destructive">
            <AlertCircle />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={() => void confirm()} loading={loading}>
            <Trash2 />
            Delete user
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
